import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import {
    Container,
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Avatar,
    IconButton,
    Button,
    ButtonGroup,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    Stack,
    Paper,
    CircularProgress,
    Backdrop,
    Slider,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Switch,
    Stepper,
    Step,
    StepLabel,
    Tabs,
    Tab
} from '@mui/material';
import {
    Group as GroupIcon,
    RestartAlt as RestartAltIcon,
    EmojiEvents as EmojiEventsIcon,
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
    SportsEsports as SportsEsportsIcon,
    HelpOutline as HelpOutlineIcon,
    Tune as TuneIcon,
    VolumeUp as VolumeUpIcon,
    VolumeOff as VolumeOffIcon,
    Vibration as VibrationIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Palette as PaletteIcon
} from '@mui/icons-material';
import { getAnimationSpeed } from '../config/animationConfig';
import soundVibrationService from '../services/SoundVibrationService';
import Header from './Header';
import adMobService from '../services/AdMobService';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

// Game mode constants
const GAME_MODES = {
    CLASSIC: 'classic',      // Alternating Ronda and Baciga
    RONDA_ONLY: 'ronda',     // Only Ronda
    BACIGA_ONLY: 'baciga'    // Only Baciga
};

const AVAILABLE_AVATARS = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
    '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
    '😎', '🤓', '🧐', '🤠', '🥳', '🥸', '😏', '😒',
    '🤔', '🤨', '😐', '😑', '😶', '🙄', '😬', '🤥',
    '👻', '👽', '🤖', '💀', '☠️', '👹', '👺', '🤡',
    '💩', '👶', '👧', '🧒', '👦', '👨', '👩', '🧑',
    '👴', '👵', '🧓', '👨‍⚕️', '👩‍⚕️', '👨‍🎓', '👩‍🎓', '👨‍🏫',
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼',
    '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔'
];

const GameSetup = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // GameSetup allows any orientation - no lock needed

    // Fixed 4 players, configurable game points
    const [players, setPlayers] = useState([
        { id: 1, name: '', avatar: '😀' },
        { id: 2, name: '', avatar: '👹' },
        { id: 3, name: '', avatar: '🤓' },
        { id: 4, name: '', avatar: '👺' }
    ]);
    const [gamePoints, setGamePoints] = useState(2);
    const [gameMode, setGameMode] = useState(GAME_MODES.CLASSIC);
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);
    const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [tableColor, setTableColor] = useState('#257241');
    const [cardBack, setCardBack] = useState('001');
    const [animationSpeed, setAnimationSpeed] = useState(1.0);
    const [difficulty, setDifficulty] = useState('NORMAL');
    const [aiHelpAnchor, setAiHelpAnchor] = useState(null);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [vibrationEnabled, setVibrationEnabled] = useState(true);

    // Load player names and avatars from localStorage on mount
    useEffect(() => {
        const savedPlayers = localStorage.getItem('playerSetup');
        if (savedPlayers) {
            try {
                const parsedPlayers = JSON.parse(savedPlayers);
                setPlayers(parsedPlayers);
            } catch (error) {
                debugError('Error loading player setup from localStorage:', error);
            }
        }
    }, []);

    // Load settings from localStorage on mount
    useEffect(() => {
        const savedColor = localStorage.getItem('tableColor');
        if (savedColor) setTableColor(savedColor);

        const savedCardBack = localStorage.getItem('cardBack');
        if (savedCardBack) setCardBack(savedCardBack);

        const savedSpeed = getAnimationSpeed();
        setAnimationSpeed(savedSpeed);

        const savedDifficulty = localStorage.getItem('difficulty');
        if (savedDifficulty) setDifficulty(savedDifficulty);

        const savedSoundEnabled = localStorage.getItem('soundEnabled');
        setSoundEnabled(savedSoundEnabled === null ? true : savedSoundEnabled === 'true');

        const savedVibrationEnabled = localStorage.getItem('vibrationEnabled');
        setVibrationEnabled(savedVibrationEnabled === null ? true : savedVibrationEnabled === 'true');
    }, []);

    // Settings handlers
    const handleDifficultyChange = (newDifficulty) => {
        setDifficulty(newDifficulty);
        localStorage.setItem('difficulty', newDifficulty);
        window.dispatchEvent(new CustomEvent('difficultyChanged', { detail: newDifficulty }));
    };

    const handleColorChange = (e) => {
        const newColor = e.target.value;
        setTableColor(newColor);
        localStorage.setItem('tableColor', newColor);
        window.dispatchEvent(new CustomEvent('tableColorChanged', { detail: newColor }));
    };

    const handleCardBackChange = (newCardBack) => {
        setCardBack(newCardBack);
        localStorage.setItem('cardBack', newCardBack);
        window.dispatchEvent(new CustomEvent('cardBackChanged', { detail: newCardBack }));
    };

    const handleAnimationSpeedChange = (event, newValue) => {
        setAnimationSpeed(newValue);
        localStorage.setItem('animationSpeed', newValue.toString());
        window.dispatchEvent(new CustomEvent('animationSpeedChanged', { detail: newValue }));
    };

    const handleSoundToggle = (event) => {
        const enabled = event.target.checked;
        setSoundEnabled(enabled);
        soundVibrationService.setSoundEnabled(enabled);
    };

    const handleVibrationToggle = (event) => {
        const enabled = event.target.checked;
        setVibrationEnabled(enabled);
        soundVibrationService.setVibrationEnabled(enabled);
    };

    const speedMarks = [
        { value: 0.2, label: t('settings.animation_speed_slow') },
        { value: 1.0, label: t('settings.animation_speed_normal') },
        { value: 3.0, label: t('settings.animation_speed_fast') },
    ];

    // AI Help popover data
    const rondaRows = [
        { key: 'ronda_play_card' },
        { key: 'ronda_send' },
        { key: 'ronda_counter' },
    ];
    const bacigaRows = [
        { key: 'baciga_vote' },
        { key: 'baciga_discard' },
        { key: 'baciga_hide' },
        { key: 'baciga_wildcard' },
        { key: 'baciga_stand' },
    ];

    // AdMob interstitial state
    const [isAdLoading, setIsAdLoading] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [adReady, setAdReady] = useState(false);
    const isWebPlatform = Capacitor.getPlatform() === 'web';

    // Safety timeout for the loading spinner (10 seconds)
    const AD_LOADING_TIMEOUT_MS = 10000;

    // Check if interstitial ad is ready (already prepared in Home component)
    // If not ready, try to prepare it here as fallback
    useEffect(() => {
        const checkOrPrepareAd = async () => {
            try {
                // First check if ad is already ready (prepared from Home)
                if (adMobService.isInterstitialReady()) {
                    debugLog('Interstitial ad already ready from Home');
                    setAdReady(true);
                    return;
                }

                // If not ready, prepare it as fallback
                debugLog('Interstitial ad not ready, preparing in GameSetup...');
                await adMobService.initialize();
                const ready = await adMobService.prepareInterstitial();
                setAdReady(ready);
                debugLog('Interstitial ad ready from GameSetup:', ready);
            } catch (error) {
                debugLog('AdMob interstitial check/preparation failed:', error);
                setAdReady(false);
            }
        };
        checkOrPrepareAd();
    }, []);

    // Web interstitial callback is now registered in App.jsx at the global level

    // Handle navigation after ad is dismissed
    const navigateToGame = useCallback((navigationData) => {
        const { startRoute, finalPlayers, gamePoints: gp, gameMode: gm } = navigationData;
        navigate(startRoute, {
            state: {
                players: finalPlayers,
                gamePoints: gp,
                gameMode: gm
            }
        });
    }, [navigate]);

    // Safety timeout for ad loading - if ad doesn't dismiss in time, navigate anyway
    useEffect(() => {
        if (!isAdLoading || !pendingNavigation) return;

        const timeoutId = setTimeout(() => {
            debugLog('Ad loading timeout reached, navigating directly');
            setIsAdLoading(false);
            navigateToGame(pendingNavigation);
            setPendingNavigation(null);
            // Re-prepare the next interstitial in background
            adMobService.prepareInterstitial().then(ready => setAdReady(ready));
        }, AD_LOADING_TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
    }, [isAdLoading, pendingNavigation, navigateToGame]);

    // Listen for interstitial dismissed event and navigate
    useEffect(() => {
        if (!pendingNavigation) return;

        // For web platform, we need to listen for the dismissed event from AdMobService
        if (isWebPlatform) {
            const listeners = adMobService.addInterstitialListeners({
                onDismissed: () => {
                    setIsAdLoading(false);
                    navigateToGame(pendingNavigation);
                    setPendingNavigation(null);
                    // Re-prepare the next interstitial
                    adMobService.prepareInterstitial().then(ready => setAdReady(ready));
                }
            });
            return () => listeners.remove();
        }

        // For native platforms
        const listeners = adMobService.addInterstitialListeners({
            onDismissed: () => {
                setIsAdLoading(false);
                navigateToGame(pendingNavigation);
                setPendingNavigation(null);
                // Re-prepare the next interstitial
                adMobService.prepareInterstitial().then(ready => setAdReady(ready));
            },
            onFailed: () => {
                setIsAdLoading(false);
                navigateToGame(pendingNavigation);
                setPendingNavigation(null);
            }
        });

        return () => listeners.remove();
    }, [pendingNavigation, navigateToGame, isWebPlatform]);

    const handlePlayerNameChange = (index, name) => {
        const newPlayers = [...players];
        newPlayers[index].name = name;
        setPlayers(newPlayers);
        // Save to localStorage
        localStorage.setItem('playerSetup', JSON.stringify(newPlayers));
    };

    const handleAvatarClick = (index) => {
        setSelectedPlayerIndex(index);
        setShowAvatarSelector(true);
    };

    const handleAvatarSelect = (avatar) => {
        if (selectedPlayerIndex !== null) {
            const newPlayers = [...players];
            newPlayers[selectedPlayerIndex].avatar = avatar;
            setPlayers(newPlayers);
            // Save to localStorage
            localStorage.setItem('playerSetup', JSON.stringify(newPlayers));
        }
        setShowAvatarSelector(false);
        setSelectedPlayerIndex(null);
    };

    const handleResetPlayers = () => {
        // Reset players
        const resetPlayers = [
            { id: 1, name: '', avatar: '😀' },
            { id: 2, name: '', avatar: '👹' },
            { id: 3, name: '', avatar: '🤓' },
            { id: 4, name: '', avatar: '👺' }
        ];
        setPlayers(resetPlayers);
        localStorage.setItem('playerSetup', JSON.stringify(resetPlayers));

        // Reset game settings
        setGamePoints(2);
        setGameMode(GAME_MODES.CLASSIC);
        setDifficulty('NORMAL');
        localStorage.setItem('difficulty', 'NORMAL');
        window.dispatchEvent(new CustomEvent('difficultyChanged', { detail: 'NORMAL' }));

        // Reset customization
        const defaultTableColor = '#257241';
        setTableColor(defaultTableColor);
        localStorage.setItem('tableColor', defaultTableColor);
        window.dispatchEvent(new CustomEvent('tableColorChanged', { detail: defaultTableColor }));

        const defaultCardBack = '001';
        setCardBack(defaultCardBack);
        localStorage.setItem('cardBack', defaultCardBack);
        window.dispatchEvent(new CustomEvent('cardBackChanged', { detail: defaultCardBack }));

        const defaultAnimationSpeed = 1.0;
        setAnimationSpeed(defaultAnimationSpeed);
        localStorage.setItem('animationSpeed', defaultAnimationSpeed.toString());
        window.dispatchEvent(new CustomEvent('animationSpeedChanged', { detail: defaultAnimationSpeed }));

        // Reset sound and vibration
        setSoundEnabled(true);
        soundVibrationService.setSoundEnabled(true);

        setVibrationEnabled(true);
        soundVibrationService.setVibrationEnabled(true);
    };

    const handleStartGame = async () => {
        // If no names provided, use default names
        const finalPlayers = players.map((player, index) => ({
            ...player,
            name: player.name.trim() || t(`setup.player_placeholder_${index + 1}`)
        }));

        // Determine starting route based on game mode
        const startRoute = gameMode === GAME_MODES.BACIGA_ONLY ? '/baciga' : '/ronda';

        const navigationData = {
            startRoute,
            finalPlayers,
            gamePoints,
            gameMode
        };

        // Check if ad is ready before trying to show it
        if (!adReady || !adMobService.isInterstitialReady()) {
            debugLog('Ad not ready, navigating directly without ad');
            navigateToGame(navigationData);
            // Try to prepare next ad in background
            adMobService.prepareInterstitial().then(ready => setAdReady(ready));
            return;
        }

        // Show loading spinner
        setIsAdLoading(true);
        setPendingNavigation(navigationData);

        // Try to show interstitial ad (with built-in timeout)
        const adShown = await adMobService.showInterstitial(5000);

        if (!adShown) {
            // Ad failed to show, navigate directly
            debugLog('Interstitial ad failed to show, navigating directly');
            setIsAdLoading(false);
            setPendingNavigation(null);
            navigateToGame(navigationData);
            // Re-prepare the next interstitial in background
            adMobService.prepareInterstitial().then(ready => setAdReady(ready));
        }
        // If adShown is true, the ad is showing and navigation will happen via onDismissed listener
    };

    const handleBack = () => {
        navigate('/');
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handlePrevious = () => {
        setActiveStep((prev) => prev - 1);
    };

    const steps = [
        { label: t('setup.step_players'), icon: <GroupIcon /> },
        { label: t('setup.step_game'), icon: <SportsEsportsIcon /> },
        { label: t('setup.step_customize'), icon: <PaletteIcon /> }
    ];

    return (
        <>
            {/* Web Interstitial Ad is now handled globally in App.jsx */}

            {/* Loading overlay while interstitial ad is showing */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)'
                }}
                open={isAdLoading}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" color="inherit">
                        {t('common.loading', 'Cargando...')}
                    </Typography>
                </Box>
            </Backdrop>

            <Header
                title={t('setup.title')}
                showSettings={false}
                onBack={handleBack}
                onReset={handleResetPlayers}
            />

            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', bgcolor: 'transparent', position: 'relative' }}>
                <Container maxWidth={{ xs: 'sm', md: 'lg' }} sx={{ mt: { xs: 0.5, sm: 1 }, mb: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.5 } }}>
                    {/* Step Content */}
                    <Card elevation={3}>
                        {/* Tabs as step headers */}
                        <Tabs
                            value={activeStep}
                            onChange={(e, newValue) => setActiveStep(newValue)}
                            variant="fullWidth"
                            sx={{
                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                minHeight: { xs: 36, sm: 48 },
                                '& .MuiTab-root': {
                                    minHeight: { xs: 36, sm: 48 },
                                    py: { xs: 0.5, sm: 1 },
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            {steps.map((step, index) => (
                                <Tab
                                    key={index}
                                    icon={React.cloneElement(step.icon, {
                                        sx: { fontSize: { xs: 16, sm: 20 } }
                                    })}
                                    iconPosition="start"
                                    label={step.label}
                                />
                            ))}
                        </Tabs>

                        <CardContent sx={{ py: { xs: 1.5, sm: 2.5 }, px: { xs: 1.5, sm: 2.5 }, minHeight: { xs: 280, sm: 340 } }}>
                            {/* Step 1: Players */}
                            {activeStep === 0 && (
                                <Box>
                                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                                        {/* Pareja 1 - Left Column */}
                                        <Grid item xs={6}>
                                            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, sm: 1.5 }, textAlign: 'center', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                                {t('setup.team')} 1
                                            </Typography>
                                            <Stack spacing={{ xs: 1, sm: 1.5 }}>
                                                {[0, 2].map((index) => {
                                                    const player = players[index];
                                                    return (
                                                        <Paper
                                                            key={player.id}
                                                            elevation={2}
                                                            sx={{
                                                                p: { xs: 0.75, sm: 1.25 },
                                                                bgcolor: 'rgba(102, 126, 234, 0.05)',
                                                                borderRadius: 1.5,
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: { xs: 0.75, sm: 1.25 },
                                                                borderLeft: '4px solid #667eea'
                                                            }}
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: '#667eea',
                                                                    width: { xs: 28, sm: 36 },
                                                                    height: { xs: 28, sm: 36 },
                                                                    fontWeight: 'bold',
                                                                    fontSize: { xs: '0.85rem', sm: '1rem' }
                                                                }}
                                                            >
                                                                {player.id}
                                                            </Avatar>

                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder={t(`setup.player_placeholder_${player.id}`)}
                                                                value={player.name}
                                                                onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                                                                inputProps={{ maxLength: 20 }}
                                                                sx={{
                                                                    bgcolor: 'white',
                                                                    borderRadius: 1,
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                                                        borderRadius: 1,
                                                                    }
                                                                }}
                                                            />

                                                            <Avatar
                                                                sx={{
                                                                    width: { xs: 36, sm: 44 },
                                                                    height: { xs: 36, sm: 44 },
                                                                    bgcolor: 'white',
                                                                    border: '3px solid rgba(102, 126, 234, 0.3)',
                                                                    cursor: 'pointer',
                                                                    fontSize: { xs: '1.3rem', sm: '1.6rem' },
                                                                    transition: 'transform 0.2s',
                                                                    '&:hover': { transform: 'scale(1.1)', borderColor: '#667eea' }
                                                                }}
                                                                onClick={() => handleAvatarClick(index)}
                                                            >
                                                                {player.avatar}
                                                            </Avatar>
                                                        </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        </Grid>

                                        {/* Pareja 2 - Right Column */}
                                        <Grid item xs={6}>
                                            <Typography variant="h6" fontWeight="bold" color="error" sx={{ mb: { xs: 1, sm: 1.5 }, textAlign: 'center', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                                {t('setup.team')} 2
                                            </Typography>
                                            <Stack spacing={{ xs: 1, sm: 1.5 }}>
                                                {[1, 3].map((index) => {
                                                    const player = players[index];
                                                    return (
                                                        <Paper
                                                            key={player.id}
                                                            elevation={2}
                                                            sx={{
                                                                p: { xs: 0.75, sm: 1.25 },
                                                                bgcolor: 'rgba(245, 87, 108, 0.05)',
                                                                borderRadius: 1.5,
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: { xs: 0.75, sm: 1.25 },
                                                                borderLeft: '4px solid #f5576c'
                                                            }}
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: '#f5576c',
                                                                    width: { xs: 28, sm: 36 },
                                                                    height: { xs: 28, sm: 36 },
                                                                    fontWeight: 'bold',
                                                                    fontSize: { xs: '0.85rem', sm: '1rem' }
                                                                }}
                                                            >
                                                                {player.id}
                                                            </Avatar>

                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder={t(`setup.player_placeholder_${player.id}`)}
                                                                value={player.name}
                                                                onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                                                                inputProps={{ maxLength: 20 }}
                                                                sx={{
                                                                    bgcolor: 'white',
                                                                    borderRadius: 1,
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                                                        borderRadius: 1,
                                                                    }
                                                                }}
                                                            />

                                                            <Avatar
                                                                sx={{
                                                                    width: { xs: 36, sm: 44 },
                                                                    height: { xs: 36, sm: 44 },
                                                                    bgcolor: 'white',
                                                                    border: '3px solid rgba(245, 87, 108, 0.3)',
                                                                    cursor: 'pointer',
                                                                    fontSize: { xs: '1.3rem', sm: '1.6rem' },
                                                                    transition: 'transform 0.2s',
                                                                    '&:hover': { transform: 'scale(1.1)', borderColor: '#f5576c' }
                                                                }}
                                                                onClick={() => handleAvatarClick(index)}
                                                            >
                                                                {player.avatar}
                                                            </Avatar>
                                                        </Paper>
                                                    );
                                                })}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Step 2: Game Settings */}
                            {activeStep === 1 && (
                                <Grid container spacing={{ xs: 2, sm: 3 }}>
                                    {/* Left Column */}
                                    <Grid item xs={6}>
                                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                            {/* Game Mode */}
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    {t('setup.game_mode_label')}
                                                </Typography>
                                                <Stack direction="row" spacing={{ xs: 0.5, sm: 0.75 }}>
                                                    {[
                                                        { mode: GAME_MODES.CLASSIC, icon: '🔄', label: t('setup.game_mode_classic') },
                                                        { mode: GAME_MODES.RONDA_ONLY, icon: '🎯', label: t('setup.game_mode_ronda') },
                                                        { mode: GAME_MODES.BACIGA_ONLY, icon: '🃏', label: t('setup.game_mode_baciga') }
                                                    ].map(({ mode, icon, label }) => (
                                                        <Paper
                                                            key={mode}
                                                            elevation={1}
                                                            onClick={() => setGameMode(mode)}
                                                            sx={{
                                                                flex: 1,
                                                                p: { xs: 0.5, sm: 0.75 },
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: { xs: 0.3, sm: 0.5 },
                                                                cursor: 'pointer',
                                                                bgcolor: gameMode === mode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.05)',
                                                                border: `2px solid ${gameMode === mode ? '#667eea' : 'rgba(102, 126, 234, 0.2)'}`,
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                                    borderColor: '#667eea',
                                                                }
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>{icon}</Typography>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                color={gameMode === mode ? 'primary' : 'text.secondary'}
                                                                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, textAlign: 'center' }}
                                                            >
                                                                {label}
                                                            </Typography>
                                                        </Paper>
                                                    ))}
                                                </Stack>
                                            </Box>

                                            {/* Game Points */}
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    {t('setup.game_points_label')}
                                                </Typography>
                                                <Stack direction="row" spacing={{ xs: 0.5, sm: 0.75 }}>
                                                    {[2, 4].map((points) => (
                                                        <Paper
                                                            key={points}
                                                            elevation={1}
                                                            onClick={() => setGamePoints(points)}
                                                            sx={{
                                                                flex: 1,
                                                                p: { xs: 0.5, sm: 0.75 },
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: { xs: 0.3, sm: 0.5 },
                                                                cursor: 'pointer',
                                                                bgcolor: gamePoints === points ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.05)',
                                                                border: `2px solid ${gamePoints === points ? '#667eea' : 'rgba(102, 126, 234, 0.2)'}`,
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                                    borderColor: '#667eea',
                                                                }
                                                            }}
                                                        >
                                                            <Typography variant="h6" fontWeight="bold" color={gamePoints === points ? 'primary' : 'text.secondary'} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                                                                {points}
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="bold" color={gamePoints === points ? 'primary' : 'text.secondary'} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                                                                {t('setup.game_points_label_short')}
                                                            </Typography>
                                                        </Paper>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </Grid>

                                    {/* Right Column */}
                                    <Grid item xs={6}>
                                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                            {/* Difficulty */}
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
                                                        {t('settings.difficulty')}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => setAiHelpAnchor(e.currentTarget)}
                                                        sx={{ p: 0.2 }}
                                                    >
                                                        <HelpOutlineIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                                                    </IconButton>
                                                </Box>
                                                <Stack direction="row" spacing={{ xs: 0.5, sm: 0.75 }}>
                                                    {[
                                                        { key: 'EASY', icon: '🟢', color: 'success' },
                                                        { key: 'NORMAL', icon: '🟡', color: 'warning' },
                                                        { key: 'HARD', icon: '🔴', color: 'error' }
                                                    ].map(({ key, icon, color }) => (
                                                        <Button
                                                            key={key}
                                                            onClick={() => handleDifficultyChange(key)}
                                                            variant={difficulty === key ? 'contained' : 'outlined'}
                                                            color={difficulty === key ? color : 'primary'}
                                                            size="small"
                                                            sx={{
                                                                flex: 1,
                                                                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                                                                py: { xs: 0.5, sm: 0.6 },
                                                                flexDirection: 'row',
                                                                px: { xs: 0.4, sm: 0.75 },
                                                                minWidth: 0,
                                                                gap: { xs: 0.3, sm: 0.4 }
                                                            }}
                                                        >
                                                            <Box component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                                                                {icon}
                                                            </Box>
                                                            {t(`settings.difficulty_${key.toLowerCase()}`)}
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            </Box>

                                            {/* Animation Speed */}
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    {t('settings.animation_speed')}
                                                </Typography>
                                                <Box sx={{ px: { xs: 0.75, sm: 1 }, py: { xs: 0.5, sm: 0.75 } }}>
                                                    <Slider
                                                        value={animationSpeed}
                                                        onChange={handleAnimationSpeedChange}
                                                        min={0.2}
                                                        max={3}
                                                        step={0.1}
                                                        marks={speedMarks}
                                                        valueLabelDisplay="auto"
                                                        valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiSlider-markLabel': { fontSize: { xs: '0.6rem', sm: '0.7rem' } },
                                                            '& .MuiSlider-thumb': { width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 } }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Step 3: Customization */}
                            {activeStep === 2 && (
                                <Grid container spacing={{ xs: 2, sm: 3 }}>
                                    {/* Left Column */}
                                    <Grid item xs={6}>
                                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                            {/* Table Color */}
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    {t('settings.table_color')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 } }}>
                                                    <TextField
                                                        type="color"
                                                        value={tableColor}
                                                        onChange={handleColorChange}
                                                        sx={{
                                                            width: { xs: 60, sm: 80 },
                                                            '& input': { p: { xs: 0.5, sm: 0.75 }, height: { xs: 32, sm: 40 }, cursor: 'pointer' }
                                                        }}
                                                        size="small"
                                                    />
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' }, fontFamily: 'monospace' }}>
                                                        {tableColor}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Card Back */}
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    {t('settings.card_back')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, justifyContent: 'flex-start' }}>
                                                    {['001', '002', '003'].map((back) => (
                                                        <Box
                                                            key={back}
                                                            onClick={() => handleCardBackChange(back)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.2s',
                                                                '&:hover': { transform: 'scale(1.05)' }
                                                            }}
                                                        >
                                                            <img
                                                                src={`assets/images/deck/${back}.png`}
                                                                alt={`${t('settings.card_back')} ${back}`}
                                                                style={{
                                                                    width: window.innerWidth < 600 ? 32 : 48,
                                                                    height: window.innerWidth < 600 ? 48 : 72,
                                                                    objectFit: 'cover',
                                                                    border: cardBack === back ? '3px solid #667eea' : '2px solid #ccc',
                                                                    borderRadius: 4,
                                                                    display: 'block',
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </Grid>

                                    {/* Right Column */}
                                    <Grid item xs={6}>
                                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                                            {/* Sound & Vibration */}
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, mb: { xs: 0.75, sm: 1.25 } }}>
                                                    {t('settings.sound_vibration')}
                                                </Typography>
                                                <Stack spacing={{ xs: 0.75, sm: 1 }}>
                                                    <Paper
                                                        elevation={1}
                                                        sx={{
                                                            p: { xs: 0.75, sm: 1.25 },
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                                                            {soundEnabled ? <VolumeUpIcon color="primary" sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <VolumeOffIcon color="disabled" sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                                                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
                                                                {t('settings.sound')}
                                                            </Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={soundEnabled}
                                                            onChange={handleSoundToggle}
                                                            color="primary"
                                                            size="small"
                                                        />
                                                    </Paper>
                                                    <Paper
                                                        elevation={1}
                                                        sx={{
                                                            p: { xs: 0.75, sm: 1.25 },
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                                                            <VibrationIcon color={vibrationEnabled ? 'primary' : 'disabled'} sx={{ fontSize: { xs: 18, sm: 24 } }} />
                                                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
                                                                {t('settings.vibration')}
                                                            </Typography>
                                                        </Box>
                                                        <Switch
                                                            checked={vibrationEnabled}
                                                            onChange={handleVibrationToggle}
                                                            color="primary"
                                                            size="small"
                                                        />
                                                    </Paper>
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Container>
            </Box>

            {/* Navigation Buttons - Fixed at bottom */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 1, sm: 2 }
                }}
            >
                <Container maxWidth={{ xs: 'sm', md: 'lg' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        {/* Show Previous button only on steps 2 and 3 */}
                        {activeStep > 0 ? (
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />}
                                onClick={handlePrevious}
                                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, py: { xs: 0.4, sm: 0.6 }, px: { xs: 0.75, sm: 1.25 } }}
                            >
                                {t('setup.previous')}
                            </Button>
                        ) : (
                            <Box />
                        )}

                        {activeStep < steps.length - 1 ? (
                            <Button
                                variant="contained"
                                endIcon={<ArrowForwardIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />}
                                onClick={handleNext}
                                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, py: { xs: 0.4, sm: 0.6 }, px: { xs: 0.75, sm: 1.25 } }}
                            >
                                {t('setup.next')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                endIcon={<PlayArrowIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                                onClick={handleStartGame}
                                sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                    px: { xs: 1.25, sm: 2 },
                                    py: { xs: 0.5, sm: 0.75 },
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                    }
                                }}
                            >
                                {t('setup.start_game')}
                            </Button>
                        )}
                    </Box>
                </Container>
            </Box>

            {/* AI Help Popover */}
            <Popover
                open={Boolean(aiHelpAnchor)}
                anchorEl={aiHelpAnchor}
                onClose={() => setAiHelpAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        maxWidth: '80vw',
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        p: 2,
                        borderRadius: 2,
                    }
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t('ai_help.title')}</Typography>

                {/* Ronda Table */}
                <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mt: 1 }}>{t('ai_help.ronda_title')}</Typography>
                <Table size="small" sx={{ mb: 2, '& td, & th': { px: 1, py: 0.5, fontSize: '0.7rem', lineHeight: 1.3 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', minWidth: 70 }}>{t('ai_help.decision')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>{t('ai_help.easy')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'warning.main' }}>{t('ai_help.normal')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>{t('ai_help.hard')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rondaRows.map(({ key }) => (
                            <TableRow key={key}>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t(`ai_help.${key}`)}</TableCell>
                                <TableCell>{t(`ai_help.${key}_easy`)}</TableCell>
                                <TableCell>{t(`ai_help.${key}_normal`)}</TableCell>
                                <TableCell>{t(`ai_help.${key}_hard`)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Baciga Table */}
                <Typography variant="subtitle2" fontWeight="bold" color="primary">{t('ai_help.baciga_title')}</Typography>
                <Table size="small" sx={{ mb: 1, '& td, & th': { px: 1, py: 0.5, fontSize: '0.7rem', lineHeight: 1.3 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', minWidth: 70 }}>{t('ai_help.decision')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>{t('ai_help.easy')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'warning.main' }}>{t('ai_help.normal')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>{t('ai_help.hard')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bacigaRows.map(({ key }) => (
                            <TableRow key={key}>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t(`ai_help.${key}`)}</TableCell>
                                <TableCell>{t(`ai_help.${key}_easy`)}</TableCell>
                                <TableCell>{t(`ai_help.${key}_normal`)}</TableCell>
                                <TableCell>{t(`ai_help.${key}_hard`)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('ai_help.note')}
                </Typography>
            </Popover>

            {/* Avatar Selector Modal */}
            <Dialog
                open={showAvatarSelector}
                onClose={() => setShowAvatarSelector(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, maxHeight: '80vh' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" fontWeight="bold">{t('setup.avatar_selector_title')}</Typography>
                    <IconButton onClick={() => setShowAvatarSelector(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                        {AVAILABLE_AVATARS.map((avatar, index) => (
                            <Grid item xs={3} sm={2} key={index} display="flex" justifyContent="center">
                                <Box
                                    onClick={() => handleAvatarSelect(avatar)}
                                    sx={{
                                        fontSize: '2rem',
                                        cursor: 'pointer',
                                        width: 50,
                                        height: 50,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        transition: 'transform 0.2s',
                                        bgcolor: selectedPlayerIndex !== null && players[selectedPlayerIndex].avatar === avatar ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                                        border: selectedPlayerIndex !== null && players[selectedPlayerIndex].avatar === avatar ? '2px solid #667eea' : '2px solid transparent',
                                        '&:hover': {
                                            transform: 'scale(1.2)',
                                            bgcolor: 'rgba(102, 126, 234, 0.1)'
                                        }
                                    }}
                                >
                                    {avatar}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default GameSetup;
